/**
 * @file PriceSyncUpdatesModal.js
 * @version 1.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-10-21
 * @description Orders and POs still on another cost after a cost price change
 *              was posted — listed, ticked, and changed only when the person
 *              says so ("Update selected" / "Leave them").
 *
 * 1.1.0 (2026-10-12, Cam): rebuilt on effective dates. A line takes the posted
 * cost change for its item whose effective date is the latest on or before
 * its delivery date — so a change posted today but dated 1 October reaches
 * orders delivered from 1 October, and a change dated later never reaches an
 * earlier delivery. Orders with a live customer invoice are never listed; a
 * billed PO is listed UNTICKED with its bill's cost beside it (the bill is our
 * source of truth for cost). Only the cost changes — the sale price is kept
 * (1.0 cleared it). A PO line's new cost includes its additives; a Xero-made
 * PO's cost is sent to Xero; one Xero holds as billed comes back with the
 * prompt and "Update cost from Xero".
 *
 * 1.2.0 (2026-10-12): the sale price is CLEARED again by default on every
 * order line whose cost moves (Cam's standing instruction of 2026-09-18 —
 * carrying the old sale price against a new cost quietly changes the margin;
 * clearing puts the line back in front of someone in Price Orders). A toggle,
 * on by default, "Clear sale prices so these orders are repriced in Price
 * Orders"; off keeps them. Sent as `clear_sale_prices`.
 *
 * Opened three ways:
 *   - straight after Sync to Xero (Price Changes), for the changes just posted;
 *   - "Review orders" on a posted change, or "Orders to review" (Price
 *     Changes) — `scope.changeIds`;
 *   - Price Orders: the ticked orders ("Update cost prices" — `scope.orderIds`,
 *     only those orders and the POs bought for them), or the header's "Cost
 *     Price Changes" with no scope, which offers a chooser (changes with
 *     orders to review, or the changes posted on a day).
 * The list is worked out afresh every time it opens; applying sends the rows
 * as shown and the server changes only those still on the cost shown.
 *
 *   GET  /api/price-sync-updates/changes   POST /preview   POST /apply   POST /leave
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { useAccess } from '../hooks/useAccess';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { formatEffectiveDate } from '../utils/priceChangeDates';
import './TableStyles.css';
import './PriceSyncUpdatesModal.css';

const money = (v) => (v === null || v === undefined || v === '' ? '—' : formatPrice(v));

const OUTCOME_LABEL = {
  updated: 'Updated',
  skipped: 'Left',
  failed: 'Not changed',
  billed_in_xero: 'Billed in Xero',
  deferred: 'Not done yet'
};

const PENDING = 'pending';

/** The chooser's options: changes with orders to review, then each day changes were posted. */
export function chooserOptions(changes) {
  const list = changes || [];
  const pending = list.filter((c) => c.reprice_review === PENDING);
  const days = new Map();
  for (const c of list) {
    const d = c.posted_day;
    if (!d) continue;
    days.set(d, (days.get(d) || 0) + 1);
  }
  return [
    { value: PENDING, label: `Changes with orders to review (${pending.length})` },
    ...[...days.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([d, n]) => ({
      value: `day:${d}`,
      label: `Posted ${formatEffectiveDate(d)} — ${n} item${n === 1 ? '' : 's'}`
    }))
  ];
}

/** The change ids a chooser value stands for. */
export function chosenChangeIds(changes, value) {
  const list = changes || [];
  if (value === PENDING) return list.filter((c) => c.reprice_review === PENDING).map((c) => c.id);
  if (String(value || '').startsWith('day:')) {
    const d = value.slice(4);
    return list.filter((c) => c.posted_day === d).map((c) => c.id);
  }
  return [];
}

/** The rows ticked to begin with: everything changeable except billed POs. */
export function defaultTicks(rows) {
  return new Set((rows || []).filter((r) => r.default_checked && r.changeable).map((r) => r.key));
}

/** Measures the footer's slide-out buttons once the Modal has rendered them. */
function Body({ deps, children }) {
  useSlideOutButton(deps);
  return <>{children}</>;
}

function PriceSyncUpdatesModal({ isOpen, scope = null, intro = null, onClose, onApplied }) {
  const { can } = useAccess();
  const seeCosts = can('costs.view');

  const [changes, setChanges] = useState([]);
  const [chosen, setChosen] = useState(PENDING);
  const [plan, setPlan] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [resyncing, setResyncing] = useState(null);
  const [resynced, setResynced] = useState({});
  // Clear the sale prices of the order lines whose cost moves (the default).
  const [clearSale, setClearSale] = useState(true);

  const chooser = !scope;

  // The chooser's list of posted changes.
  useEffect(() => {
    if (!isOpen || !chooser || !seeCosts) return;
    setChosen(PENDING);
    fetch(`${API_URL}/api/price-sync-updates/changes`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.error || 'Could not load the price changes');
        setChanges(d.changes || []);
      })
      .catch((e) => setError(e.message));
  }, [isOpen, chooser, seeCosts]);

  const activeScope = useMemo(() => {
    if (scope) return scope;
    const ids = chosenChangeIds(changes, chosen);
    return ids.length ? { changeIds: ids } : null;
  }, [scope, changes, chosen]);
  const scopeKey = JSON.stringify(activeScope || null);

  const loadPlan = useCallback(async () => {
    if (!activeScope || !seeCosts) { setPlan(null); setChecked(new Set()); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/price-sync-updates/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: activeScope })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not work out the orders affected');
      setPlan(data);
      setChecked(defaultTicks(data.rows));
    } catch (e) {
      setError(e.message);
      setPlan(null);
    } finally {
      setLoading(false);
    }
    // scopeKey stands for activeScope's contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, seeCosts]);

  useEffect(() => {
    if (!isOpen) return;
    setResults(null);
    setResynced({});
    setClearSale(true);
    loadPlan();
  }, [isOpen, loadPlan]);

  const rows = useMemo(() => plan?.rows || [], [plan]);
  const changeable = rows.filter((r) => r.changeable);
  const allChecked = changeable.length > 0 && changeable.every((r) => checked.has(r.key));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(changeable.map((r) => r.key)));
  const toggleOne = (key, on) => setChecked((prev) => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });
  const billedCount = rows.filter((r) => r.billed).length;
  const blockedCount = rows.filter((r) => !r.changeable).length;
  const changeIds = activeScope?.changeIds || null;

  const apply = async () => {
    const picked = rows.filter((r) => checked.has(r.key)).map((r) => ({ key: r.key, from: r.from, to: r.to }));
    if (!picked.length) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/price-sync-updates/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: activeScope, rows: picked, clear_sale_prices: clearSale })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Nothing was updated');
      setResults(data);
      onApplied && onApplied(data);
      await loadPlan();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // "Leave them": for price changes, recorded as reviewed; for ticked orders, just closed.
  const leave = async () => {
    if (changeIds && changeIds.length && seeCosts) {
      try {
        await fetch(`${API_URL}/api/price-sync-updates/leave`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ changeIds })
        });
      } catch (e) {
        // Leaving changes nothing; the marker simply stays.
      }
    }
    onClose && onClose();
  };

  // Billed in Xero: once the cost has been changed there, take it back (cost only).
  const resync = async (r) => {
    if (!r.xero_purchase_order_id) return;
    setResyncing(r.key);
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(r.xero_purchase_order_id)}/resync-cost-from-xero`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines: [{ key: r.key.split(':').slice(2).join(':'), unitAmount: r.to }], confirm: true })
      });
      const data = await res.json();
      setResynced((prev) => ({
        ...prev,
        [r.key]: (!res.ok || !data.success) ? [data.error || 'The cost could not be taken from Xero'] : (data.messages || ['Taken from Xero.'])
      }));
    } catch (e) {
      setResynced((prev) => ({ ...prev, [r.key]: [`The cost could not be taken from Xero: ${e.message}`] }));
    } finally {
      setResyncing(null);
    }
  };

  if (!isOpen) return null;

  const busy = saving || !!resyncing;
  const footer = (
    <div className="buttons slideout">
      <div>
        <button
          className="btn-slideout"
          onClick={leave}
          disabled={busy}
          title={changeIds ? 'Change nothing, and mark these price changes as reviewed' : 'Change nothing'}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">{results ? 'Close' : 'Leave them'}</span>
        </button>
      </div>
      <div>
        <button
          className="btn-slideout"
          onClick={apply}
          disabled={busy || loading || !checked.size || !seeCosts}
          title={!seeCosts ? 'Needs permission to see costs' : checked.size ? 'Put the new cost on the ticked lines' : 'Nothing is ticked'}
          data-write-action
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">price_change</span></div>
          <span className="btn-label">
            {saving ? 'Updating…' : `Update selected (${checked.size})`}
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Orders on another cost"
      className="price-sync-updates"
      size="xl"
      footer={footer}
    >
      <Body deps={[isOpen, rows.length, checked.size, saving, !!results]}>
        {intro && <p className="psu-intro">{intro}</p>}
        <p className="psu-explainer">
          Each line takes the posted cost change for its product whose effective date is the latest on or before
          its delivery date — a change never reaches a delivery before its date. Orders already on a customer
          invoice are not listed. A PO line's new cost includes its additives, and its order line follows it.
          Unless you switch it off below, each order line whose cost changes has its <strong>sale price
          cleared</strong> so it comes back through Pricing & Costing to be priced against the new cost — keeping the
          old sale price would change the margin without anyone deciding to.
        </p>

        {!seeCosts && <p className="psu-empty">Repricing orders needs permission to see costs.</p>}

        {seeCosts && chooser && (
          <div className="psu-controls">
            <div className="filter-group">
              <label>Price changes</label>
              <Dropdown value={chosen} onChange={setChosen} options={chooserOptions(changes)} />
            </div>
          </div>
        )}

        {seeCosts && plan && rows.length > 0 && (
          <div className="psu-clear-sale">
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="psu-clear-sale"
                  type="checkbox"
                  checked={clearSale}
                  onChange={(e) => setClearSale(e.target.checked)}
                  disabled={busy}
                />
                <label htmlFor="psu-clear-sale"></label>
              </div>
            </div>
            <label htmlFor="psu-clear-sale" className="psu-clear-sale-label">
              Clear sale prices so these orders are repriced in Pricing & Costing
            </label>
          </div>
        )}

        {seeCosts && plan && rows.length > 0 && (
          <div className="psu-count">
            {rows.length} line{rows.length === 1 ? '' : 's'} on another cost · {checked.size} ticked
            {plan.changes?.length ? ` · from ${plan.changes.length} price change${plan.changes.length === 1 ? '' : 's'}` : ''}
          </div>
        )}

        {seeCosts && (billedCount > 0 || blockedCount > 0) && !loading && (
          <div className="psu-review-notice">
            <span className="material-symbols-rounded">warning</span>
            <span>
              {billedCount > 0 && (
                <>
                  <strong>{billedCount} {billedCount === 1 ? 'line is' : 'lines are'} on a billed PO.</strong>{' '}
                  The supplier's bill is our record of what it cost, so {billedCount === 1 ? 'it is' : 'they are'} left
                  unticked, with the bill's cost beside {billedCount === 1 ? 'it' : 'them'}. Tick one only if the bill is wrong.{' '}
                </>
              )}
              {blockedCount > 0 && `${blockedCount} cannot be changed here — the reason is on the line.`}
            </span>
          </div>
        )}

        {results && (
          <div className="psu-results" role="status">
            <p className="psu-results-counts">
              {['updated', 'skipped', 'failed', 'billed_in_xero', 'deferred']
                .filter((k) => results.counts?.[k])
                .map((k) => `${results.counts[k]} ${OUTCOME_LABEL[k].toLowerCase()}`)
                .join(' · ') || 'Nothing was changed'}
              {results.counts?.sale_prices_cleared
                ? ` · ${results.counts.sale_prices_cleared} sale price${results.counts.sale_prices_cleared === 1 ? '' : 's'} cleared — reprice ${results.counts.sale_prices_cleared === 1 ? 'it' : 'them'} in Pricing & Costing`
                : ''}
            </p>
            <ul>
              {(results.results || []).filter((r) => r.outcome !== 'updated').map((r) => (
                <li key={r.key} className={`outcome-${r.outcome}`}>
                  <span className="psu-outcome">{OUTCOME_LABEL[r.outcome] || r.outcome}</span>
                  <span className="psu-so">{r.order_number || r.purchase_order_number || '—'}</span>
                  {r.item_code && <span className="account-badge-inline">{r.item_code}</span>}
                  <span className="psu-message">{r.message}</span>
                  {r.outcome === 'billed_in_xero' && (
                    <div className="psu-resync">
                      <button
                        className="btn-slideout"
                        onClick={() => resync(r)}
                        disabled={busy}
                        data-write-action
                        title="Once the cost is changed on the PO in Xero: take Xero's cost onto this PO (the cost only)"
                      >
                        <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                        <span className="btn-label">{resyncing === r.key ? 'Reading Xero…' : 'Update cost from Xero'}</span>
                      </button>
                      {(resynced[r.key] || []).map((m, i) => <p key={i}>{m}</p>)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="psu-error" role="alert">{error}</p>}

        {seeCosts && (loading ? <LoadingSpinner /> : !activeScope ? (
          <p className="psu-empty">
            {chooser
              ? (chosen === PENDING ? 'No posted change has orders waiting to be reviewed — choose a posting day above.' : 'No cost changes were posted that day.')
              : 'Nothing chosen.'}
          </p>
        ) : !rows.length ? (
          <p className="psu-empty">
            {results ? 'Nothing else is on another cost.' : 'Every order and PO this covers is already on its cost (or already invoiced).'}
          </p>
        ) : (
          <div className="table-container psu-table-wrap">
            <table className="data-table psu-table">
              <thead>
                <tr>
                  <th className="checkbox-col no-sort">
                    <TickCheckbox id="psu-all" checked={allChecked} onChange={toggleAll} ariaLabel="Tick every line that can be changed" />
                  </th>
                  <th>Order / PO</th>
                  <th>Customer / Supplier</th>
                  <th>Item</th>
                  <th>Delivery</th>
                  <th>Price change</th>
                  <th className="num">Qty</th>
                  <th className="num">Cost now → new</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const on = checked.has(r.key);
                  return (
                    <tr key={r.key} className={`line-row${on ? ' selected' : ''}${r.billed || !r.changeable ? ' needs-review' : ''}`}>
                      <td className="checkbox-col">
                        <TickCheckbox
                          id={`psu-${r.key}`}
                          checked={on}
                          disabled={!r.changeable}
                          title={r.changeable ? undefined : r.reason}
                          onChange={(e) => toggleOne(r.key, e.target.checked)}
                          ariaLabel={`Update ${r.item_code || 'this line'} on ${(r.kind === 'po' ? r.purchase_order_number : r.order_number) || 'this line'}`}
                        />
                      </td>
                      <td className="psu-refs">
                        {r.kind === 'po'
                          ? <span className="psu-so">{r.purchase_order_number}{r.xero_made && <span className="psu-tag" title="Made in Xero — the cost is sent to Xero">XERO</span>}</span>
                          : <span className="psu-so">{r.order_number}</span>}
                        {r.kind === 'po' && r.order_number && <span className="psu-po">for {r.order_number}</span>}
                        {r.kind === 'order' && r.purchase_order_number && <span className="psu-po">{r.purchase_order_number}</span>}
                      </td>
                      <td>
                        <span className="psu-customer-line">
                          {r.account_no && <span className="account-badge-inline">{r.account_no}</span>}
                          <span className="psu-customer-name">{r.party || '—'}</span>
                        </span>
                      </td>
                      <td>
                        <span className="psu-item">
                          {r.item_code && <span className="account-badge-inline">{r.item_code}</span>}
                          <span className="psu-item-name">{r.description}</span>
                        </span>
                      </td>
                      <td className="psu-date">{formatEffectiveDate(r.delivery_date)}</td>
                      <td className="psu-date" title={r.change_label}>
                        from {formatEffectiveDate(r.change_effective_date)}
                        {r.backdated && <span className="psu-tag psu-tag-back" title="Posted after its effective date">BACKDATED</span>}
                      </td>
                      <td className="num">{r.quantity == null ? '—' : formatQuantity(r.quantity)}</td>
                      <td className="num">
                        <span className={`psu-was${on ? ' is-replaced' : ''}`} title={r.from_item ? "The item's current cost — the line has none of its own" : undefined}>
                          {money(r.from)}
                        </span>
                        <span className="psu-now" title={r.additive_cost ? `Item ${formatPrice(r.to - r.additive_cost)} + additives ${formatPrice(r.additive_cost)}` : undefined}>
                          {money(r.to)}
                        </span>
                        {r.bill_cost != null && <span className="psu-bill">Bill {money(r.bill_cost)}</span>}
                      </td>
                      <td className="psu-note">
                        {!r.changeable && <span className="psu-blocked">{r.reason}</span>}
                        {(r.notes || []).map((n, i) => <span key={i}>{n}</span>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {seeCosts && plan?.left?.length > 0 && !loading && (
          <p className="psu-left">
            Not listed: {plan.left.map((l) => `${l.count} ${l.reason.toLowerCase()}`).join(' · ')}.
          </p>
        )}
      </Body>
    </Modal>
  );
}

export default PriceSyncUpdatesModal;
